import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthService from '../services/authService';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Mail,
  Send,
  Key,
  CheckCircle,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { usePageTitle } from '../hooks/usePageTitle';

/**
 * Password Reset Page - World-Class UI Implementation
 *
 * Features:
 * - 2-column layout (form + visual content)
 * - SALONHUB logo integration
 * - shadcn/ui Design System
 * - Responsive design (mobile: 1-column, desktop: 2-column)
 * - WCAG 2.1 AA compliant
 * - Smooth micro-interactions
 *
 * @returns Password reset page component
 */
const PasswordReset: React.FC = () => {
  usePageTitle('パスワードリセット');
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateEmail(email)) {
      setError('有効なメールアドレスを入力してください');
      return;
    }

    setLoading(true);

    try {
      await AuthService.requestPasswordReset(email);
      setLoading(false);
      navigate('/password-reset/success', { state: { email } });
    } catch (err) {
      setLoading(false);
      const errorMessage = err instanceof Error ? err.message : 'パスワードリセット要求の送信に失敗しました。もう一度お試しください。';
      setError(errorMessage);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 min-h-screen">
      {/* LEFT COLUMN: Password Reset Form */}
      <div className="flex flex-col justify-center p-4 md:p-8 bg-gray-50">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <div className="max-w-[480px] mx-auto w-full">
            {/* SALONHUB Logo */}
            <img
              src="/assets/logos/salon-hub-logo.png"
              alt="SALON HUB"
              className="w-full max-w-[175px] h-auto mb-6 transition-transform duration-300 cursor-pointer hover:scale-105"
              onClick={handleBackToLogin}
            />

            {/* Title */}
            <h1 className="scroll-m-20 text-2xl font-bold tracking-tight text-gray-900 mb-2 leading-tight">
              パスワードをリセット
            </h1>

            <p className="text-base text-gray-600 leading-relaxed mb-6">
              登録されたメールアドレスを入力してください。
              <br />
              パスワードリセット用のリンクをお送りします。
            </p>

            {/* Password Reset Form */}
            <form onSubmit={handlePasswordReset}>
              {/* Error Alert */}
              {error && (
                <Alert variant="destructive" className="mb-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Email Field */}
              <div className="space-y-2 mb-6">
                <Label htmlFor="email">メールアドレス</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    autoFocus
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (error) setError('');
                    }}
                    placeholder="example@salonhub.co.jp"
                    required
                    className="pl-10 bg-white min-h-[56px]"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full min-h-[56px] py-2.5 mb-4"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    送信中...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    リセット用リンクを送信
                  </>
                )}
              </Button>

              {/* Back to Login */}
              <Button
                type="button"
                variant="outline"
                className="w-full min-h-[56px] py-2.5"
                onClick={handleBackToLogin}
              >
                ログインページに戻る
              </Button>
            </form>
          </div>
        </motion.div>
      </div>

      {/* RIGHT COLUMN: Visual Content */}
      <div className="hidden md:flex flex-col justify-center items-center p-8 bg-gradient-to-br from-primary via-primary/90 to-primary/80 text-white relative overflow-hidden">
        <div className="absolute top-[-50%] right-[-50%] w-[200%] h-[200%] bg-[radial-gradient(circle,rgba(255,255,255,0.1)_0%,transparent_70%)] animate-[pulse_8s_ease-in-out_infinite]" />

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
          className="z-10 max-w-[500px] w-full"
        >
          {/* Main Message */}
          <h2 className="scroll-m-20 text-2xl md:text-3xl font-bold tracking-tight mb-4 text-center leading-[1.3] [text-shadow:0_2px_4px_rgba(0,0,0,0.1)]">
            パスワードリセット
            <br />
            簡単3ステップ
          </h2>

          <p className="text-lg opacity-95 leading-relaxed mb-8 text-center">
            セキュアなプロセスで安全にパスワードをリセットできます
          </p>

          {/* Steps */}
          <div className="mt-6">
            {/* Step 1 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="mb-6 flex items-start gap-3">
                <div className="min-w-[48px] min-h-[48px] rounded-full bg-white/20 flex items-center justify-center backdrop-blur-[10px]">
                  <Mail className="w-6 h-6" />
                </div>
                <div>
                  <h6 className="font-semibold text-lg mb-1">
                    1. メールアドレスを入力
                  </h6>
                  <p className="text-sm opacity-90 leading-relaxed">
                    登録されたメールアドレスを入力してください
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Step 2 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <div className="mb-6 flex items-start gap-3">
                <div className="min-w-[48px] min-h-[48px] rounded-full bg-white/20 flex items-center justify-center backdrop-blur-[10px]">
                  <Send className="w-6 h-6" />
                </div>
                <div>
                  <h6 className="font-semibold text-lg mb-1">
                    2. メールを確認
                  </h6>
                  <p className="text-sm opacity-90 leading-relaxed">
                    リセット用のリンクが記載されたメールが届きます
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Step 3 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <div className="flex items-start gap-3">
                <div className="min-w-[48px] min-h-[48px] rounded-full bg-white/20 flex items-center justify-center backdrop-blur-[10px]">
                  <Key className="w-6 h-6" />
                </div>
                <div>
                  <h6 className="font-semibold text-lg mb-1">
                    3. 新しいパスワードを設定
                  </h6>
                  <p className="text-sm opacity-90 leading-relaxed">
                    リンクをクリックして新しいパスワードを設定
                  </p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Security Note */}
          <div className="mt-8 p-4 bg-white/10 rounded-lg backdrop-blur-[10px]">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5" />
              <h6 className="font-semibold text-base">
                セキュリティについて
              </h6>
            </div>
            <p className="text-sm opacity-90 leading-relaxed">
              リセット用リンクの有効期限は24時間です。
              <br />
              セキュリティ上の理由により、1回のみ使用可能です。
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PasswordReset;
